import { z } from 'zod';
import {
  DecisionChannel,
  DecisionValue,
  Id,
  InterventionStatus,
  LongText,
  MediaKind,
  MediaVisibility,
  MessageAuthorType,
  Money,
  Timestamp,
} from './common.js';

/**
 * Vague 6 — protection des données (RGPD) : durées de conservation, information du client, accord
 * pour la photo du client, opposition aux e-mails, export et effacement d'un client.
 * Source : `docs/rgpd/CONSERVATION-ET-DROITS.md` (durées proposées, à faire valider par un juriste).
 */

// ─── Durées de conservation ─────────────────────────────────────────────────

/**
 * Durées de conservation appliquées par la tâche quotidienne `maintenance.retention-purge`
 * (fonction SQL `retention_purge()`, migration 0008, durées **écrites dans la fonction** : l'application
 * ne peut pas les raccourcir). Un test vérifie que la fonction applique exactement ces valeurs.
 * `dossierYears` et `customerPhotoIdleYears` sont affichées (politique) mais **pas encore appliquées** (P2).
 */
export const RETENTION = {
  /** Fiche, véhicule, interventions : après la dernière intervention (réglage par garage, 3 à 10 ans). Non appliquée. */
  dossierYearsDefault: 5,
  dossierYearsMin: 3,
  dossierYearsMax: 10,
  /** Preuve des décisions : IP et navigateur de la décision effacés au terme (le reste de la preuve reste). */
  proofYears: 5,
  /** IP du journal du dossier (`audit_log.ip`) mise à blanc. */
  auditIpMonths: 12,
  /** Sessions supprimées après expiration ou révocation (IP et navigateur compris). */
  sessionDaysAfterEnd: 30,
  /** Jetons « mot de passe oublié » supprimés après expiration ou usage. */
  passwordResetHoursAfterEnd: 24,
  /** Invitations supprimées après expiration, acceptation ou révocation (le journal garde l'événement). */
  invitationDaysAfterEnd: 90,
  /** Événements techniques supprimés après distribution. */
  outboxDaysAfterDispatch: 30,
  /** Journal des e-mails supprimé après l'envoi (ou la création s'il n'est jamais parti). */
  emailDeliveryMonths: 12,
  /** Adresse destinataire d'un lien portail effacée après son expiration, si le lien n'a servi à aucune décision. */
  portalRecipientMonthsAfterExpiry: 12,
  /** Abonnements push sans envoi réussi depuis (date de création à défaut). */
  pushSubscriptionIdleDays: 90,
  /** Travaux terminés de la file pg-boss (identifiants seulement), réglé explicitement sur chaque file. */
  queueCompletedDays: 7,
  /** Photo du client : au plus 3 ans sans nouvelle intervention. Non appliquée (P2). */
  customerPhotoIdleYears: 3,
  /**
   * 08/10/2026 (compte rendu de revente) : durée par défaut du lien de compte rendu (réglage du garage,
   * 30 à 3 650 jours, révocable). Ne contient aucune coordonnée du client.
   */
  reportLinkDaysDefault: 3650,
  reportLinkDaysMax: 3650,
  /**
   * Règle d'alignement : un dossier (intervention, véhicule, travaux, propositions, photos publiées) dont le
   * lien de compte rendu est valide (non expiré, non révoqué) est conservé **au moins jusqu'à l'expiration de
   * ce lien**, même au-delà de `dossierYears` ; la fiche client (coordonnées) suit la règle générale. À
   * respecter par la purge des dossiers (P2, non appliquée).
   */
  reportKeepsDossier: true,
} as const;

/** Catégories traitées par `retention_purge()` (une ligne de résultat par catégorie, nombres seulement). */
export const RETENTION_CATEGORIES = [
  'audit_ip',
  'decision_ip',
  'sessions',
  'password_reset_tokens',
  'invitations',
  'outbox_events',
  'email_deliveries',
  'portal_recipients',
  'push_subscriptions',
] as const;
export type RetentionCategory = (typeof RETENTION_CATEGORIES)[number];

/** Durée de conservation des dossiers (réglage du garage) : en années. */
export const DossierRetentionYears = z.int().min(RETENTION.dossierYearsMin).max(RETENTION.dossierYearsMax);

// ─── Information du client (politique de confidentialité du portail) ────────

/**
 * Page publique de la politique : `${PORTAL_URL}${PRIVACY_PAGE_PATH}/<clé publique du garage>` (web, L5).
 * Clé aléatoire non devinable (`garage_branding.privacy_public_key`), jamais le slug (pas d'énumération
 * des garages, comme pour le manifeste de la vague 5). La page ne contient aucune donnée de client.
 */
export const PRIVACY_PAGE_PATH = '/confidentialite';
/** Date de la version du texte de la politique (`docs/rgpd/POLITIQUE-CONFIDENTIALITE-PORTAIL.md`). */
export const PRIVACY_POLICY_DATE = '2026-10-01';

/** Variables de la politique (route publique `public.privacy`) : identité du garage et durées. Aucune donnée de client. */
export const PrivacyPolicyOutput = z.object({
  garage: z.object({
    displayName: z.string(),
    /** Raison sociale (réglage `legalName`) ; `null` : le nom affiché fait foi. */
    legalName: z.string().nullable(),
    address: z.string().nullable(),
    phone: z.string().nullable(),
    /**
     * Contact « données personnelles » du garage (réglage `privacyContactEmail`), sinon `null` : jamais
     * l'adresse de réponse ni celle du propriétaire (audit S40). À défaut, afficher `editor.email`.
     */
    email: z.string().nullable(),
    logoUrl: z.string().nullable(),
    primaryColor: z.string(),
  }),
  productName: z.string(),
  /** Éditeur de la plateforme (sous-traitant) : configuration `EDITOR_LEGAL_NAME`, `EDITOR_ADDRESS`. */
  editor: z.object({
    legalName: z.string().nullable(),
    address: z.string().nullable(),
    /** Correctifs (S40) : contact générique de l'éditeur (`EDITOR_PRIVACY_EMAIL`), affiché à défaut du garage. */
    email: z.string().nullable().optional(),
  }),
  durations: z.object({
    portalTokenTtlDays: z.int(),
    portalAccessAfterReleaseHours: z.int(),
    reportLinkTtlDays: z.int(),
    dossierYears: z.int(),
    proofYears: z.int(),
    ipMonths: z.int(),
    emailLogMonths: z.int(),
  }),
  /** Date de la version du texte (AAAA-MM-JJ). */
  updatedAt: z.string(),
  /** Adresse absolue de cette page (pied des e-mails, information à l'entrée). */
  policyUrl: z.string(),
});
export type PrivacyPolicyOutput = z.infer<typeof PrivacyPolicyOutput>;

/** Clé publique de la politique d'un garage : 32 caractères hexadécimaux minuscules. */
export const PRIVACY_KEY_PATTERN = /^[0-9a-f]{32}$/;
export const PrivacyParams = z.strictObject({ key: z.string().regex(PRIVACY_KEY_PATTERN) });

// ─── Pages légales de la plateforme (vague 7) ───────────────────────────────

/**
 * Pages légales **de la plateforme** (éditeur), publiques et sans marque de garage, sur l'espace garage et
 * sur la page de suivi : à ne pas confondre avec la notice **du garage** (`/confidentialite/<clé>`).
 * Texte source : `docs/legal/` (à faire valider par un juriste).
 */
export const PLATFORM_LEGAL_PATHS = {
  legalNotice: '/mentions-legales',
  privacy: PRIVACY_PAGE_PATH,
} as const;
/** Date de la version des textes `docs/legal/MENTIONS-LEGALES.md` et `POLITIQUE-CONFIDENTIALITE.md`. */
export const PLATFORM_LEGAL_DATE = '2026-10-05';

/**
 * Variables des pages légales (route publique `public.legal`) : identité de l'éditeur et adresses des
 * services, **toutes issues de la configuration** (`EDITOR_*`, `APP_URL`, `PORTAL_URL`, `WEBSITE_URL`,
 * `MAIL_FROM_DOMAIN`). `null` : non renseigné (l'interface affiche « à compléter »). Aucune donnée de garage.
 */
export const PublicLegalOutput = z.strictObject({
  productName: z.string(),
  productNamePro: z.string(),
  editor: z.strictObject({
    legalName: z.string().nullable(),
    tradeName: z.string().nullable(),
    legalForm: z.string().nullable(),
    address: z.string().nullable(),
    /** `null` : immatriculation en cours (l'interface l'indique). */
    siren: z.string().nullable(),
    phone: z.string().nullable(),
    publicationDirector: z.string().nullable(),
    contactEmail: z.string().nullable(),
    /** Contact « données personnelles » ; à défaut, `contactEmail`. */
    privacyEmail: z.string().nullable(),
  }),
  sites: z.strictObject({
    /** Site de présentation (vitrine), facultatif. */
    website: z.string().nullable(),
    /** Espace garage (équipes). */
    app: z.string(),
    /** Page de suivi des clients. */
    portal: z.string(),
    /** Domaine d'envoi des e-mails (aucun site web). */
    mailDomain: z.string(),
  }),
  /** Durée de validité d'une session de l'équipe (`SESSION_TTL_DAYS`). */
  sessionTtlDays: z.int(),
  /** Date de la version des textes (AAAA-MM-JJ). */
  updatedAt: z.string(),
});
export type PublicLegalOutput = z.infer<typeof PublicLegalOutput>;

// ─── Photo du client : accord ───────────────────────────────────────────────

/**
 * Texte d'information par défaut, lu au client avant la prise de vue (réglage `customerPhotoNotice`,
 * `null` = ce texte). Toute modification du texte incrémente `customerPhotoNoticeVersion`.
 */
export const DEFAULT_CUSTOMER_PHOTO_NOTICE =
  'Avec votre accord, le garage prend une photo de vous pour vous reconnaître à l’accueil. ' +
  'Elle reste interne au garage : elle n’apparaît jamais sur votre page de suivi ni dans un e-mail. ' +
  'Elle est conservée jusqu’à ce que vous demandiez son retrait, et au plus 3 ans sans nouvelle intervention. ' +
  'Vous pouvez retirer votre accord à tout moment auprès du garage ; la photo est alors supprimée.';

export const CustomerPhotoNotice = LongText.min(20).max(2000);

/**
 * Champs texte du formulaire multipart de `customers.setPhoto` (partie `consent` **avant** la partie
 * `file`) : l'accord du client est obligatoire. Sans lui : 422 `customer_photo_consent_required`.
 */
export const CustomerPhotoUploadFields = z.strictObject({
  consent: z.literal('true', { error: 'L’accord du client est obligatoire pour prendre sa photo' }),
});

/** Accord enregistré : date (horloge du serveur) et version du texte d'information lu au client. */
export const PhotoConsentOutput = z.object({
  givenAt: Timestamp,
  noticeVersion: z.int(),
});
export type PhotoConsentOutput = z.infer<typeof PhotoConsentOutput>;

// ─── Effacement ─────────────────────────────────────────────────────────────

/** Mention qui remplace les données personnelles effacées (fiche, traces immuables). */
export const ERASED_MARK = '[effacé]';

/** Confirmation explicite, saisie par l'utilisateur (« EFFACER ») : l'effacement est définitif. */
export const CustomerEraseInput = z.strictObject({ confirm: z.literal('EFFACER') });

/** Résultat d'un effacement : nombres seulement. */
export const CustomerEraseOutput = z.object({
  erasedAt: Timestamp,
  interventions: z.int().nonnegative(),
  vehicles: z.int().nonnegative(),
  photos: z.int().nonnegative(),
  messages: z.int().nonnegative(),
  decisions: z.int().nonnegative(),
  auditEntries: z.int().nonnegative(),
});
export type CustomerEraseOutput = z.infer<typeof CustomerEraseOutput>;

// ─── Export ─────────────────────────────────────────────────────────────────

/** Nom du fichier JSON dans l'archive d'export (les photos sont sous `photos/`). */
export const CUSTOMER_EXPORT_JSON = 'donnees.json';
export const CUSTOMER_EXPORT_FORMAT = 'garage-customer-export';

const ExportPhoto = z.object({
  id: Id,
  kind: MediaKind,
  visibility: MediaVisibility,
  caption: z.string().nullable(),
  createdAt: Timestamp,
  /** Chemin dans l'archive (`photos/<id>.jpg`, copie réencodée sans métadonnées), ou `null` si le fichier manque. */
  file: z.string().nullable(),
});

/**
 * Contenu de `donnees.json` dans l'archive de `customers.export` (droit d'accès et portabilité).
 * Toutes les données qui concernent le client dans **ce** garage, montants compris (la route exige
 * `customer.manage` **et** `amounts.view`). Jamais de jeton, d'empreinte ni de donnée d'un autre client.
 */
export const CustomerExport = z.object({
  format: z.literal(CUSTOMER_EXPORT_FORMAT),
  version: z.literal(1),
  exportedAt: Timestamp,
  garage: z.object({ displayName: z.string(), legalName: z.string().nullable(), address: z.string().nullable() }),
  customer: z.object({
    id: Id,
    kind: z.enum(['person', 'company']),
    civility: z.enum(['mme', 'm']).nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    companyName: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    emailOptOut: z.boolean(),
    photoConsent: PhotoConsentOutput.nullable(),
    /** Photo de fiche du client (`photos/client.jpg`), ou `null`. */
    photo: z.string().nullable(),
    /** Remarque de la fiche (interne au garage, 02/10/2026), ou `null`. */
    notes: z.string().nullable(),
    createdAt: Timestamp,
  }),
  vehicles: z.array(
    z.object({
      id: Id,
      plate: z.string(),
      make: z.string(),
      model: z.string(),
      version: z.string().nullable(),
      vin: z.string().nullable(),
      /** Photo de fiche du véhicule (`photos/vehicule-<id>.jpg`), ou `null`. */
      photo: z.string().nullable(),
      /** Remarque de la fiche (interne au garage, 02/10/2026), ou `null`. */
      notes: z.string().nullable(),
      createdAt: Timestamp,
    }),
  ),
  /** Rendez-vous (interventions planifiées), entrés, annulés ou à venir. */
  plannedInterventions: z.array(
    z.object({ plannedFor: z.string(), status: z.string(), customerRequest: z.string(), createdAt: Timestamp }),
  ),
  interventions: z.array(
    z.object({
      id: Id,
      vehicleId: Id,
      status: InterventionStatus,
      receivedAt: Timestamp,
      entryMileage: z.int(),
      exitMileage: z.int().nullable(),
      customerRequest: z.string(),
      entryConditionNotes: z.string().nullable(),
      storageTermsText: z.string(),
      storageWaivedAt: Timestamp.nullable(),
      readyAt: Timestamp.nullable(),
      releasedAt: Timestamp.nullable(),
      closedAt: Timestamp.nullable(),
      // 08/10/2026 (revue de la documentation) : préconisations et prochaine échéance (compte rendu, 0022).
      recommendations: z.string().nullable(),
      nextDue: z.object({ label: z.string().nullable(), date: z.string().nullable(), mileage: z.number().int().nullable() }),
      workItems: z.array(
        z.object({
          label: z.string(),
          reference: z.string().nullable(),
          quantity: z.string(),
          unitPriceHt: Money.nullable(),
          vatRate: z.string(),
          performedAt: Timestamp,
        }),
      ),
      complements: z.array(
        z.object({
          title: z.string(),
          versions: z.array(
            z.object({
              version: z.int(),
              sentAt: Timestamp,
              lines: z.array(z.record(z.string(), z.unknown())),
              totalHt: Money,
              totalVat: Money,
              totalTtc: Money,
              decisions: z.array(
                z.object({
                  decision: DecisionValue,
                  channel: DecisionChannel,
                  decidedAt: Timestamp,
                  totalTtc: Money,
                  note: z.string().nullable(),
                  /** Modification demandée (`change_requested`). */
                  comment: z.string().nullable(),
                  /** Données de l'appareil enregistrées avec la décision (portail). */
                  ip: z.string().nullable(),
                  userAgent: z.string().nullable(),
                }),
              ),
            }),
          ),
        }),
      ),
      /** Messages écrits, et événements du dossier avec leur phrase rendue dans `body`. */
      messages: z.array(z.object({ kind: z.enum(['text', 'event']), authorType: MessageAuthorType, body: z.string(), createdAt: Timestamp })),
      /** Fiches de réception validées (contenu figé, empreinte) et prise de connaissance. */
      entryReports: z.array(
        z.object({
          number: z.int(),
          version: z.int(),
          validatedAt: Timestamp,
          content: z.record(z.string(), z.unknown()),
          contentSha256: z.string(),
          acknowledgment: z.object({ at: Timestamp, channel: z.enum(['portal', 'in_person']) }).nullable(),
          /** PDF dans l'archive (`fiches/<numéro>-v<version>.pdf`), ou `null`. */
          file: z.string().nullable(),
        }),
      ),
      photos: z.array(ExportPhoto),
      /** Adresses auxquelles des liens de suivi ont été envoyés, avec leurs dates (jamais le jeton). */
      portalLinks: z.array(z.object({ recipient: z.string().nullable(), createdAt: Timestamp, expiresAt: Timestamp })),
      emails: z.array(z.object({ template: z.string(), recipient: z.string(), status: z.string(), sentAt: Timestamp.nullable() })),
    }),
  ),
});
export type CustomerExport = z.infer<typeof CustomerExport>;
